import React from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import Dropdown from '../Dropdown/Dropdown';
import Input from '../Input-new/Input';
import SelectBuyer from '../SelectBuyer/SelectBuyer';
import SelectInvestor from '../SelectInvestor/SelectInvestor';
import SelectSeller from '../SelectSeller/SelectSeller';
import SelectLender from '../SelectLender/SelectLender';
import SelectTeam from '../SelectTeam-new/SelectTeam';
import SelectFirm from '../SelectFirm/SelectFirm';
import SelectAgent from '../SelectAgent/SelectAgent';
import Alert from '../Alert';
import { propertiesApi } from '../../requests/api';
import { CHUNK_SIZE, FIELDS, FIELDS_BY_ID, MAX_PROPERTIES, OWNER_FIELD, buildAttributes, describeValue, fieldId, optionLabel, ownerId, referringAttributes } from './fields';

const YES_NO_OPTIONS = [
    { value: 'y', label: 'Yes' },
    { value: 'no', label: 'No' },
];

// Closing a property does more than set a column — see PropertyService's
// Closed transition — so say so before somebody does it to 80 at once.
const CLOSED_WARNING =
    'Closing properties also archives their current owners, and flags the ones ' +
    'without a buyer as needing an owner — exactly as closing one property does.';

// The Investors tab calls this Archive: the period stays on the record, it
// just stops being the current one.
const OWNER_WARNING =
    'Setting the owner archives whoever owns each of these properties today and ' +
    'opens a new ownership period for the investor you picked.';

// Setting who is on a deal does not set what they are owed: the property panel
// re-splits the percentages as agents change, and only while the property is
// still early enough for that to be safe. Bulk sets the names and leaves the
// splits alone, which is worth saying before somebody assumes otherwise.
const AGENT_WARNING =
    'This sets who is on the deal, not the commission split. The percentages ' +
    'beside each agent are left exactly as they are — set those on the ' +
    "property's own Listing Agent or Buyside Agent tab.";

export default function BulkEditProperties({ open, properties, onClose, onSaved }) {
    // Field codes in the order the user added them, so the form does not
    // reshuffle itself as it grows.
    const [chosen, setChosen] = React.useState([]);
    const [values, setValues] = React.useState({});
    // code => what to show on the confirmation step; the value itself is an id
    // or a code the user never sees.
    const [labels, setLabels] = React.useState({});
    const [confirming, setConfirming] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    // How many properties are already written, while a chunked edit is in
    // flight — a selection of thousands is several requests, and a button that
    // says nothing for two minutes reads as a hung page.
    const [written, setWritten] = React.useState(0);

    const propertyIds = (properties || [])
        .map((property) => property && property.ARCIMEDES_ROW_ID)
        .filter(Boolean)
        .map(String);

    // A fresh edit every time it opens: last time's fields are not this time's.
    React.useEffect(() => {
        if (!open) return;
        setChosen([]);
        setValues({});
        setLabels({});
        setConfirming(false);
        setBusy(false);
        setError(null);
        setWritten(0);
    }, [open]);

    if (!open) return null;

    const remaining = FIELDS.filter((field) => chosen.indexOf(fieldId(field)) === -1);

    const addField = (id) => {
        if (!id || chosen.indexOf(id) !== -1) return;
        setChosen(chosen.concat([id]));
        setError(null);
    };

    const removeField = (id) => {
        setChosen(chosen.filter((entry) => entry !== id));
        const nextValues = { ...values };
        const nextLabels = { ...labels };
        delete nextValues[id];
        delete nextLabels[id];
        setValues(nextValues);
        setLabels(nextLabels);
        setError(null);
    };

    const setValue = (id, value, label) => {
        setValues({ ...values, [id]: value === null || value === undefined ? '' : value });
        setLabels({ ...labels, [id]: label });
        setError(null);
    };

    // Every dropdown in the app reports changes as (updatedData, …, valueKey,
    // typeKey, selectedOption); take the new value out of the copy it made.
    const handleDropdownChange = (data, optionsType, optionValue, valueKey, typeKey, reason) => {
        setValue(valueKey, data[valueKey], optionLabel(reason, data[valueKey]));
    };

    const handleInputChange = (id, value) => setValue(id, value, undefined);

    const review = () => {
        try {
            // Surfaces a bad date/number, or an owner field left empty.
            buildAttributes(chosen, values);
            ownerId(chosen, values);
            setError(null);
            setConfirming(true);
        } catch (err) {
            setError(err.message);
        }
    };

    const apply = () => {
        let attributes;
        let owner;
        let referring;

        try {
            attributes = buildAttributes(chosen, values);
            owner = ownerId(chosen, values);
            referring = referringAttributes(chosen, values);
        } catch (err) {
            setConfirming(false);
            setError(err.message);
            return;
        }

        setBusy(true);
        setWritten(0);

        // One request per CHUNK_SIZE properties, in sequence: the server takes
        // the list screen's cap per call, and running them one after another
        // keeps the writes — and the side-effect jobs behind them — at a pace
        // the queue can follow.
        const chunks = [];
        for (let start = 0; start < propertyIds.length; start += CHUNK_SIZE) {
            chunks.push(propertyIds.slice(start, start + CHUNK_SIZE));
        }

        const total = { updated: 0, missing: [], fields: [] };

        chunks.reduce(
            (previous, chunk) => previous.then(() =>
                propertiesApi.bulkUpdate(chunk, attributes, owner, referring).then((data) => {
                    total.updated += data.updated || 0;
                    total.missing = total.missing.concat(data.missing || []);
                    total.fields = data.fields || total.fields;
                    setWritten(total.updated);
                })
            ),
            Promise.resolve(),
        ).then(() => {
            setBusy(false);
            onSaved(total);
        }).catch((err) => {
            setBusy(false);
            setConfirming(false);
            // Say what did land: the chunks before the failure are written, and
            // a bare "could not apply" would send the user to redo all of it.
            const failed = err.message || 'Could not apply the bulk edit.';
            setError(total.updated
                ? `${failed} ${total.updated.toLocaleString()} of ${propertyIds.length.toLocaleString()} properties were already updated.`
                : failed);
        });
    };

    const renderEditor = (field) => {
        const id = fieldId(field);
        const value = values[id] === undefined || values[id] === null
            ? ''
            : values[id];

        switch (field.control) {
            case 'investor':
                return (
                    <SelectInvestor
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                    />
                );
            case 'buyer':
                return (
                    <SelectBuyer
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                    />
                );
            case 'seller':
                return (
                    <SelectSeller
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                    />
                );
            case 'lender':
                return (
                    <SelectLender
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                    />
                );
            // The Listing Agent / Buyside Agent pickers, without the split
            // percentage that only makes sense on one deal.
            case 'team':
                return (
                    <SelectTeam
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                        onlySummitAgents
                    />
                );
            // Agents from outside Summit — a different list from the team
            // pickers above.
            case 'agent':
                return (
                    <SelectAgent
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                    />
                );
            case 'firm':
                return (
                    <SelectFirm
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                    />
                );
            case 'yesno':
                return (
                    <Dropdown
                        options={YES_NO_OPTIONS}
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                    />
                );
            case 'dropdown':
                return (
                    <Dropdown
                        optionsType={field.optionsType}
                        optionKey={field.optionKey}
                        optionLabelKey={field.optionLabelKey}
                        value={String(value)}
                        valueKey={id}
                        label={field.label}
                        data={values}
                        handleParentChange={handleDropdownChange}
                    />
                );
            case 'date':
                return (
                    <Input
                        placeholder="MM/DD/YYYY"
                        valueType="text"
                        name={id}
                        label={field.label}
                        value={value}
                        handleParentChange={handleInputChange}
                    />
                );
            default:
                return (
                    <Input
                        valueType="text"
                        name={id}
                        label={field.label}
                        value={value}
                        handleParentChange={handleInputChange}
                    />
                );
        }
    };

    const countLabel = `${propertyIds.length.toLocaleString()} ${propertyIds.length === 1 ? 'property' : 'properties'}`;
    const tooMany = propertyIds.length > MAX_PROPERTIES;
    const closingInBulk = values.STATUS === 'Closed' && chosen.indexOf('STATUS') !== -1;
    const reassigningOwner = chosen.indexOf(OWNER_FIELD) !== -1;
    const settingAgents = chosen.some((id) => {
        const field = FIELDS_BY_ID[id];
        return Boolean(field) && (field.control === 'team' || field.control === 'agent');
    });

    return (
        <Modal show={open} onClose={busy ? () => {} : onClose} className="BulkEdit">
            <div className="BulkEdit_body">
                <h2 className="BulkEdit_title">
                    {confirming ? 'Are you sure you want to bulk edit?' : 'Bulk Edit'}
                </h2>
                <p className="BulkEdit_count">
                    {confirming
                        ? `The following changes will be applied to all ${countLabel} you selected. This cannot be undone.`
                        : `Editing ${countLabel}. Every field you set below is written to all of them.`}
                </p>

                {error ? <Alert type="error">{error}</Alert> : null}
                {tooMany ? (
                    <Alert type="error">
                        {`A bulk edit covers up to ${MAX_PROPERTIES.toLocaleString()} properties at a time. Narrow the filter, or untick some rows, and try again.`}
                    </Alert>
                ) : null}

                {confirming ? (
                    <div className="BulkEdit_summary">
                        <table>
                            <thead>
                                <tr>
                                    <th>Field</th>
                                    <th>New value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {chosen.map((id) => {
                                    const described = describeValue(id, values, labels);
                                    return (
                                        <tr key={id}>
                                            <td>{FIELDS_BY_ID[id].label}</td>
                                            <td className={described.cleared ? 'is-cleared' : ''}>
                                                {described.text}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {settingAgents ? <Alert type="error">{AGENT_WARNING}</Alert> : null}
                        {reassigningOwner ? <Alert type="error">{OWNER_WARNING}</Alert> : null}
                        {closingInBulk ? <Alert type="error">{CLOSED_WARNING}</Alert> : null}
                    </div>
                ) : (
                    <div className="BulkEdit_fields">
                        {chosen.map((id) => (
                            <div className="BulkEdit_field" key={id}>
                                <div className="BulkEdit_field_editor">
                                    {renderEditor(FIELDS_BY_ID[id])}
                                </div>
                                <button
                                    type="button"
                                    className="button-new text BulkEdit_field_remove"
                                    onClick={() => removeField(id)}
                                    title={`Do not change ${FIELDS_BY_ID[id].label}`}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}

                        <div className="BulkEdit_picker">
                            {/*
                              * Remounted on every pick: the picker's own value
                              * never changes (it is always ""), so the
                              * autocomplete keeps the label just chosen as its
                              * input text and filters the next open by it —
                              * which reads as "the field I want is missing".
                              */}
                            <Dropdown
                                key={chosen.length}
                                options={remaining.map((field) => ({
                                    value: fieldId(field),
                                    label: field.label,
                                    groupedBy: field.group,
                                }))}
                                groupBy
                                value=""
                                valueKey="ADD_FIELD"
                                label="Add a field to update"
                                placeholder="Choose a field"
                                data={{}}
                                handleParentChange={(data) => addField(data.ADD_FIELD)}
                            />
                        </div>

                        <p className="BulkEdit_hint">
                            Leave a field blank to clear it on every selected property.
                        </p>
                    </div>
                )}

                <div className="BulkEdit_actions">
                    {confirming ? (
                        <React.Fragment>
                            <button
                                type="button"
                                className={`button-new text ${busy ? 'inactive' : ''}`}
                                onClick={() => setConfirming(false)}
                                disabled={busy}
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className={`button-new positive ${busy ? 'inactive' : ''}`}
                                onClick={apply}
                                disabled={busy}
                            >
                                {busy
                                    ? (propertyIds.length > CHUNK_SIZE
                                        ? `Updating… ${written.toLocaleString()} of ${propertyIds.length.toLocaleString()}`
                                        : 'Updating…')
                                    : `Yes, update ${countLabel}`}
                            </button>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <button type="button" className="button-new text" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`button-new positive ${!chosen.length || tooMany ? 'inactive' : ''}`}
                                onClick={review}
                                disabled={!chosen.length || tooMany}
                            >
                                Review changes
                            </button>
                        </React.Fragment>
                    )}
                </div>
            </div>
        </Modal>
    );
}

BulkEditProperties.propTypes = {
    open: PropTypes.bool.isRequired,
    // The ticked rows, straight from the list.
    properties: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    // Called with { updated, missing, fields } once the server has written them.
    onSaved: PropTypes.func.isRequired,
};
